/**
 * MVP module — PY2026 parameter set.
 * Values sourced from the policy layer (cite, don't re-derive):
 *   docs/reference/mvp/scoring-methodology-py2026.md §8 (MVP guide)
 *   docs/reference/mvp/traditional-mips-scoring-methodology-py2026.md §9 (Trad guide)
 *
 * Year-scoping model (charter §4): a future year with NO policy change inherits
 * this set by reference via parametersForYear(). Fork the file when values change;
 * never edit a shipped year in place (fixtures pin it).
 */

export interface ParameterSet {
  effectiveFromPY: number;

  performanceThreshold: number;          // 75 (PY2026–2028)
  finalScoreMax: number;                 // 100

  // Quality
  mvpRequiredQualityMeasures: number;    // 4 (incl. 1 outcome / high-priority)
  traditionalRequiredQualityMeasures: number; // 6
  pointsPerMeasureMax: number;           // 10
  smallPracticeQualityBonus: number;     // 6, numerator-only
  qualityImprovementCapPP: number;       // 10 percentage points
  qualityImprovementMultiplier: number;  // ×10% → expressed as 10 (pp basis)

  // Population health (MVP foundational layer)
  popHealthMeasureIds: readonly ['479', '484'];
  popHealthDenominatorWhenScored: number; // +10

  // Cost
  costImprovementCapPP: number;          // 1 percentage point
  costPointsAtMedian: number;            // 0.10 × threshold = 7.5
  /** SD-band cut-offs, bands 1→10, as offsets from the median in σ (Trad §3.3). */
  costBandCutoffsSD: readonly number[];

  // IA
  mvpIaRequiredActivities: number;       // 1
  mvpIaPointsPerActivity: number;        // 40 (flat — full credit)
  iaCategoryMaxPoints: number;           // 40

  // CPB (mirrors mipsCompositeScoring.calculatePatientComplexityBonus)
  cpbMultiplier: number;                 // 0.2 × complexityScore
  cpbCap: number;                        // 2

  // Registration (display-only)
  registrationWindow: { opens: string; closes: string; cahpsDeadline: string };
}

export const PY2026: ParameterSet = {
  effectiveFromPY: 2026,

  performanceThreshold: 75,
  finalScoreMax: 100,

  mvpRequiredQualityMeasures: 4,
  traditionalRequiredQualityMeasures: 6,
  pointsPerMeasureMax: 10,
  smallPracticeQualityBonus: 6,
  qualityImprovementCapPP: 10,
  qualityImprovementMultiplier: 10,

  popHealthMeasureIds: ['479', '484'],
  popHealthDenominatorWhenScored: 10,

  costImprovementCapPP: 1,
  costPointsAtMedian: 7.5,
  costBandCutoffsSD: [2.75, 2.5, 2.25, 2, 1.5, 1, 0.5, -0.5, -1, -1.5],

  mvpIaRequiredActivities: 1,
  mvpIaPointsPerActivity: 40,
  iaCategoryMaxPoints: 40,

  cpbMultiplier: 0.2,
  cpbCap: 2,

  registrationWindow: { opens: '2026-04-01', closes: '2026-11-30', cahpsDeadline: '2026-06-30' },
};

const SETS: ParameterSet[] = [PY2026];

/** Nearest-prior inheritance: a year with no fork inherits the latest set ≤ year. */
export function parametersForYear(year: number): ParameterSet | null {
  const eligible = SETS.filter(s => s.effectiveFromPY <= year)
    .sort((a, b) => b.effectiveFromPY - a.effectiveFromPY);
  return eligible[0] ?? null;
}
